import { useMemo } from 'react';
import { createInfiniteQuery } from 'react-query-kit';
import { keepPreviousData } from '@tanstack/react-query';
import type { QuizsetSearchReq } from '@/api/gen/_model/quizsetSearchReq';
import { QuizsetSearchReqOrder } from '@/api/gen/_model/quizsetSearchReqOrder';
import type { QuizsetSearchReqPeriod } from '@/api/gen/_model/quizsetSearchReqPeriod';
import type { SearchResponseListSearchQuizsetRes } from '@/api/gen/_model/searchResponseListSearchQuizsetRes';
import { searchUserPublicQuizsets } from '@/api/gen/quiz-user/quiz-user.gen';
import { useSearchQuizQuery } from '@/shared/quizList/hooks/useSearchQuizQuery';
import { retryOnServiceUnavailable } from '@/shared/utils/queryRetryBudget';
export const isQuizsetSearchReqOrder = (value: unknown): value is QuizsetSearchReqOrder => {
  return typeof value === 'string' && value in QuizsetSearchReqOrder;
};

/**
 * 프로필 퀴즈 검색 조건. 인기순이면 기간(`period`)도 넘긴다 — 정렬 메뉴가 기간을 고르게 하는데 넘기지 않으면
 * 기간을 바꿔도 목록이 그대로이고, 같은 기간을 쓰는 폴더 섹션과 정렬이 어긋난다.
 */
export const getDefaultQuizsetSearchReq = ({
  searchText,
  order,
  period
}: {
  searchText: string;
  order: string;
  period?: QuizsetSearchReqPeriod;
}): QuizsetSearchReq => {
  const resolvedOrder = isQuizsetSearchReqOrder(order) ? order : QuizsetSearchReqOrder.RECENTLY;
  return {
    rootOnly: false,
    categoryFilters: {
      criteria: {
        ...(searchText?.length > 0 && {
          searchText: searchText,
          searchType: 'TITLE'
        })
      }
    },
    order: resolvedOrder,
    ...(resolvedOrder !== QuizsetSearchReqOrder.RECENTLY && period && {
      period
    }),
    limit: 20,
    page: 1
  };
};
export const useUserQuizes = (userId: string) => {
  const {
    searchText,
    order,
    period
  } = useSearchQuizQuery();
  const result = useFetchUserQuizes({
    enabled: true,
    variables: {
      userId,
      quizsetSearchReq: getDefaultQuizsetSearchReq({
        searchText,
        order,
        period
      })
    }
  });
  const flatData = useMemo(() => result.data?.pages.flatMap(page => page.data ?? []) ?? [], [result.data]);
  return {
    ...result,
    flatData,
    totalCount: result?.data?.pages[0].totalCount ?? 0,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.isFetchingNextPage
  };
};
export const useFetchUserQuizes = createInfiniteQuery<SearchResponseListSearchQuizsetRes, {
  userId: string;
  quizsetSearchReq: QuizsetSearchReq;
}>({
  queryKey: ['quizsets/search'],
  use: [retryOnServiceUnavailable],
  fetcher: async (variables, {
    pageParam,
    signal
  }) => {
    return searchUserPublicQuizsets(variables.userId, {
      ...variables.quizsetSearchReq,
      page: pageParam
    }, undefined, signal);
  },
  getNextPageParam: lastPage => {
    if (lastPage?.currentPage == null || lastPage?.currentPage === 0 || lastPage?.totalPages == null || lastPage?.totalPages === 0) {
      return undefined;
    }
    return lastPage?.currentPage < lastPage?.totalPages ? lastPage.currentPage + 1 : undefined;
  },
  initialPageParam: 1,
  placeholderData: keepPreviousData
});